import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Facebook, Instagram, Linkedin, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import TrustBadges from '../shared/TrustBadges';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-foreground text-white relative overflow-hidden border-t border-border/10">
      <TrustBadges />

      {/* Premium Ambient Background Glows */}
      <div
        className="absolute top-0 left-1/4 w-[600px] h-[300px] rounded-full opacity-10 pointer-events-none blur-[100px]"
        style={{ background: 'radial-gradient(circle, #FED202 0%, transparent 70%)' }}
      />
      <div 
        className="absolute bottom-0 right-1/4 w-[500px] h-[300px] rounded-full opacity-5 pointer-events-none blur-[100px] bg-primary" 
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 relative z-10">

        {/* TOP TRUST STRIP - Redesigned as a glass card */}
        <div className="mb-16 relative rounded-3xl bg-white/[0.03] border border-white/10 p-8 md:p-12 text-center overflow-hidden shadow-2xl">
          {/* Subtle inner glow for the card */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-full bg-gradient-to-b from-primary/10 to-transparent opacity-50 pointer-events-none blur-2xl" />
          
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-white tracking-tight">
            Learn. Build. <span className="text-primary">Get Hired.</span>
          </h2>
          <p className="text-white/60 mt-3 text-base md:text-lg max-w-2xl mx-auto">
            Industry-led training in Full Stack, AI & Cybersecurity with real career outcomes.
          </p>

          <div className="mt-8 flex flex-wrap justify-center items-center gap-x-8 gap-y-4 text-white/90 font-medium text-sm md:text-base">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-primary" />
              <span>2,000+ Students</span>
            </div>
            <div className="hidden sm:block w-1.5 h-1.5 rounded-full bg-white/20" />
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-primary" />
              <span>Industry Mentors</span>
            </div>
            <div className="hidden sm:block w-1.5 h-1.5 rounded-full bg-white/20" />
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-primary" />
              <span>Project-Based Learning</span>
            </div>
          </div>
        </div>

        {/* MAIN GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8">

          {/* BRAND (Spans 4 columns) */}
          <div className="lg:col-span-4">
            <Link to="/" className="inline-block mb-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg">
              <div className="w-44 h-12 relative flex items-center overflow-hidden">
                <img
                  src="https://www.xceededge.com/data/images/header-logo2.png"
                  alt="XceedEdge Logo"
                  className="w-full h-full object-contain object-left"
                />
              </div>
            </Link>

            <p className="text-sm text-white/60 leading-relaxed mb-8 pr-4">
              A career-focused learning platform helping students transition into high-growth tech roles through real-world training.
            </p>

            <div className="flex gap-3">
              {[
                { Icon: Instagram, label: 'Instagram', href: 'https://www.instagram.com/xceededge' },
                { Icon: Facebook, label: 'Facebook', href: 'https://www.facebook.com/people/XceedEdge/61562829604572/' },
                { Icon: Linkedin, label: 'LinkedIn', href: 'https://www.linkedin.com/company/xceededge-technologies' },
              ].map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-primary hover:text-black hover:-translate-y-1 hover:border-primary hover:shadow-[0_0_15px_rgba(254,210,2,0.3)] transition-all duration-300"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* LINKS (Spans 2 columns) */}
          <div className="lg:col-span-2">
            <h4 className="text-sm font-heading font-bold text-white uppercase tracking-wider mb-6">Explore</h4>
            <ul className="space-y-4">
              {[
                { label: 'Courses', to: '/courses' },
                { label: 'About Us', to: '/about' },
                { label: 'Blog', to: '/blog' },
                { label: 'For Colleges', to: '/for-colleges' },
                { label: 'Contact', to: '/contact' }
              ].map(link => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="group flex items-center text-sm text-white/60 hover:text-primary transition-colors duration-300"
                  >
                    <ArrowRight className="w-3 h-3 mr-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                    <span className="group-hover:translate-x-1 transition-transform duration-300 inline-block">{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* DOMAINS (Spans 3 columns) */}
          <div className="lg:col-span-3">
            <h4 className="text-sm font-heading font-bold text-white uppercase tracking-wider mb-6">Domains</h4>
            <ul className="space-y-4">
              {[
                'Full Stack Development',
                'Artificial Intelligence',
                'Cybersecurity',
                'Cloud & DevOps',
                'Data Science'
              ].map(cat => (
                <li key={cat}>
                  <Link
                    to="/courses"
                    className="group flex items-center text-sm text-white/60 hover:text-primary transition-colors duration-300"
                  >
                    <ArrowRight className="w-3 h-3 mr-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                    <span className="group-hover:translate-x-1 transition-transform duration-300 inline-block">{cat}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* NEWSLETTER (Spans 3 columns) */}
          <div className="lg:col-span-3">
            <h4 className="text-sm font-heading font-bold text-white uppercase tracking-wider mb-6">Stay Updated</h4>
            <p className="text-sm text-white/60 mb-5">
              Get career insights, course updates & industry trends delivered to your inbox.
            </p>

            <div className="flex gap-2 mb-8">
              <Input
                placeholder="Enter email"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40 rounded-xl focus-visible:ring-primary focus-visible:border-primary h-11"
              />
              <Button className="bg-primary hover:bg-primary/90 text-black font-semibold rounded-xl h-11 px-6 shadow-[0_0_15px_rgba(254,210,2,0.2)] hover:shadow-[0_0_20px_rgba(254,210,2,0.4)] transition-all">
                Join
              </Button>
            </div>

            <div className="space-y-4 text-sm text-white/60">
              <a href="mailto:info@xceededge.com" className="flex items-center gap-3 hover:text-primary transition-colors w-fit group">
                <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-primary/10 group-hover:border-primary/30 transition-colors">
                  <Mail className="w-4 h-4 text-primary" />
                </div>
                info@xceededge.com
              </a>
              <a href="tel:+91 9391650077" className="flex items-center gap-3 hover:text-primary transition-colors w-fit group">
                <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-primary/10 group-hover:border-primary/30 transition-colors">
                  <Phone className="w-4 h-4 text-primary" />
                </div>
                +91 9391650077
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM BAR */}
      <div className="border-t border-white/10 bg-black/20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-white/40">
            © {currentYear} XceedEdge. Built for future tech careers.
          </p>
          <div className="flex gap-6 text-sm text-white/50">
            <Link to="/terms" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Support</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}